/* lp-v2 runtime -- reactivates the JS-dependent GemPages components of a
 * verbatim-cloned page, plus the one behaviour this pipeline adds on top of
 * the capture (viewport-driven video autoplay). Copied verbatim by
 * pipeline/verbatim/templatize.py to `assets/lp-v2-<job>-runtime.js` and
 * loaded once, deferred, from `layout/lp-v2-blank.liquid`.
 *
 * Vanilla, no dependencies, no globals beyond one idempotence flag. It only
 * ever touches nodes it finds by their own hooks:
 *
 *   1. <gp-carousel>            -- sizes the keen-slider track per breakpoint,
 *                                  renders the dot strip GemPages leaves as
 *                                  `<!-- item render by js -->`, and wires the
 *                                  two arrows + pointer drag.
 *   2. <gp-countdown-timer>     -- ticks the evergreen clock from a deadline
 *                                  persisted in localStorage.
 *   3. .slide-sticky            -- re-adds the `.visible` class past the same
 *                                  scroll trigger the captured custom-js used.
 *   4. video[data-lp-autoplay]  -- plays a motion/GIF-replacement clip once it
 *                                  nears the viewport and pauses it again once
 *                                  it leaves, instead of every clip on the
 *                                  page downloading and decoding on load (see
 *                                  "video" below).
 *
 * Fidelity contract: at the capture breakpoint every value components 1-3
 * compute is byte-identical to the value GemPages serialized into the DOM,
 * and every write is guarded by an equality check, so the desktop render
 * cannot move. Component 4 is deliberately NOT part of that contract -- the
 * captured page always shipped these clips with a plain `autoplay` attribute,
 * and delivery.py strips it for load-speed reasons the capture knows nothing
 * about; this file is what keeps the clip playing at all.
 *
 * In the theme editor ONLY (`Shopify.designMode`), it also scrolls the preview
 * to a selected section and rebinds a section the editor has re-rendered. Both
 * are behind the same guard, so on the storefront this file binds once at
 * DOMContentLoaded and never runs a second time -- see "component lifecycle"
 * and "theme editor" below, and TEMPLATIZE_NOTES.md "Theme editor support".
 */
(function () {
  'use strict';

  if (window.__lpV2Runtime) return;
  window.__lpV2Runtime = true;

  /* ---------------------------------------------------------------- utils */

  // Exactly the breakpoints the captured GemPages stylesheets switch on.
  var MQ_MOBILE = '(max-width: 768px)';
  var MQ_TABLET = '(max-width: 1024px) and (min-width: 768px)';

  function breakpoint() {
    if (window.matchMedia(MQ_MOBILE).matches) return 'mobile';
    if (window.matchMedia(MQ_TABLET).matches) return 'tablet';
    return 'desktop';
  }

  // 307 -> "307px". Three decimals is below the sub-pixel the renderer keeps.
  function px(value) {
    return String(Math.round(value * 1000) / 1000) + 'px';
  }

  // Never write a value the element already carries: a no-op assignment still
  // dirties style state, and the captured DOM must survive untouched wherever
  // the runtime agrees with it.
  function setStyle(el, prop, value) {
    if (el.style[prop] !== value) el.style[prop] = value;
  }

  function parseJSONAttr(el, name) {
    var raw = el.getAttribute(name);
    if (!raw) return null;
    try { return JSON.parse(raw); } catch (err) { return null; }
  }

  function pick(value, bp) {
    if (value === null || typeof value !== 'object') return value;
    if (bp in value && value[bp] !== undefined && value[bp] !== '') return value[bp];
    return value.desktop;
  }

  // GemPages colour settings are either a literal hex or a design token name
  // ("bg-1", "line-3") that resolves through the .gps custom properties.
  function colour(value, fallback) {
    if (typeof value !== 'string' || !value) return fallback;
    if (value.charAt(0) === '#' || value.indexOf('rgb') === 0) return value;
    return 'var(--g-c-' + value + ', ' + fallback + ')';
  }

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  /* --------------------------------------------------- component lifecycle */

  // The theme editor re-renders a whole section on every settings edit, so a
  // component can be built more than once per page view. Two rules keep that
  // safe: an element is bound at most once, and everything a binding owns that
  // OUTLIVES its own DOM -- window listeners, intervals, animation frames -- is
  // registered for teardown so the discarded copy stops running.
  //
  // On the storefront none of this ever runs a second time: `boot(document)`
  // is called once from onReady and nothing is ever released, so the bound
  // behaviour is exactly what it was before this existed.
  var BOUND = '__lpV2Bound';
  var bindings = [];          // {node, dispose}
  var stickyRefresh = null;   // re-measure hook, set while a sticky bar is bound

  function bindOnce(node, init) {
    if (node[BOUND]) return;
    node[BOUND] = true;
    var cleanups = [];
    var ctx = {
      onCleanup: function (fn) { cleanups.push(fn); },
      listen: function (target, type, fn, opts) {
        target.addEventListener(type, fn, opts);
        cleanups.push(function () { target.removeEventListener(type, fn, opts); });
      }
    };
    bindings.push({ node: node, dispose: function () {
      node[BOUND] = false;
      // Unwound in reverse, and never allowed to throw: one component's failed
      // teardown must not strand the rest.
      for (var i = cleanups.length - 1; i >= 0; i--) {
        try { cleanups[i](); } catch (err) { /* keep unwinding */ }
      }
      cleanups.length = 0;
    } });
    // Same isolation as before: one dead component, not three.
    try { init(node, ctx); } catch (err) { /* ignore */ }
  }

  // Release every binding inside `scope`, plus any whose node the editor has
  // already swapped out of the document -- which is how the *outgoing* copy of
  // a re-rendered section is caught, since `shopify:section:load` fires after
  // the new DOM is in place and the old nodes are already detached.
  function release(scope) {
    for (var i = bindings.length - 1; i >= 0; i--) {
      var node = bindings[i].node;
      // `contains()` already returns true for the node itself, so the middle
      // test only earns its place when `scope` is not a Node at all.
      var stale = !document.contains(node) || node === scope ||
                  (scope && typeof scope.contains === 'function' && scope.contains(node));
      if (!stale) continue;
      var dispose = bindings[i].dispose;
      bindings.splice(i, 1);
      dispose();
    }
  }

  function boot(root) {
    var carousels = root.querySelectorAll('gp-carousel');
    for (var i = 0; i < carousels.length; i++) bindOnce(carousels[i], initCarousel);
    var timers = root.querySelectorAll('gp-countdown-timer');
    for (var j = 0; j < timers.length; j++) bindOnce(timers[j], initCountdown);
    var sticky = root.querySelector('.slide-sticky');
    if (sticky) bindOnce(sticky, initSticky);
    var videos = root.querySelectorAll('video[data-lp-autoplay]');
    for (var k = 0; k < videos.length; k++) bindOnce(videos[k], initVideoAutoplay);
  }

  /* ------------------------------------------------------------- carousel */

  // `calc(100% / 4 - 22.5px)` -> {perView: 4, inset: 22.5}. GemPages encodes
  // keen-slider's `spacing` as inset = spacing * (perView - 1) / perView, so
  // both numbers come straight back out of the captured custom property.
  var CALC_RE = /calc\(\s*100%\s*\/\s*([0-9.]+)\s*-\s*([0-9.]+)px\s*\)/;

  function slideGeometry(slide, trackWidth, bp) {
    var suffix = bp === 'desktop' ? '' : '-' + bp;
    var raw = slide.style.getPropertyValue('--maxw' + suffix) ||
              slide.style.getPropertyValue('--minw' + suffix);
    var match = CALC_RE.exec(raw || '');
    if (!match) return null;
    var perView = parseFloat(match[1]);
    var inset = parseFloat(match[2]);
    if (!(perView > 0)) return null;
    var spacing = perView > 1 ? (inset * perView) / (perView - 1) : 0;
    return {
      perView: perView,
      spacing: spacing,
      width: trackWidth / perView - inset
    };
  }

  function initCarousel(root, ctx) {
    var track = root.querySelector('.gem-slider');
    if (!track) return;
    var slides = Array.prototype.slice.call(
      track.querySelectorAll(':scope > .gem-slider-item'));
    if (!slides.length) return;

    var data = parseJSONAttr(root, 'gp-data') || {};
    var setting = data.setting || {};
    var uid = data.id || '';
    // GemPages serializes these two as plain booleans (only the styling keys
    // are per-breakpoint objects), so they are read once at init. If a future
    // capture makes them breakpoint-scoped, move them into layout().
    var loop = pick(setting.loop, 'desktop') !== false;
    var dragEnabled = pick(setting.enableDrag, 'desktop') !== false;

    var current = 0;
    var geom = null;

    // keen-slider's loop parks half of the off-screen slides to the left of
    // slot 0 so a backward drag has something to show. With the captured
    // 5 slides / 4 per view that is exactly one slide, which reproduces the
    // serialized `translate3d(-1565px, 0px, 0px)` on the fifth slide.
    function slotOf(index) {
      var n = slides.length;
      var rel = ((index - current) % n + n) % n;
      if (!loop) return index - current;
      var wrapAt = n - 1 - (n - geom.perView) / 2;
      return rel > wrapAt ? rel - n : rel;
    }

    function place(offset, animate) {
      for (var i = 0; i < slides.length; i++) {
        var slide = slides[i];
        var shift = slotOf(i) * (geom.width + geom.spacing) - i * geom.width;
        setStyle(slide, 'transition', animate ? 'transform 350ms ease-in' : '');
        setStyle(slide, 'transform',
                 'translate3d(' + px(shift + offset) + ', 0px, 0px)');
      }
    }

    // `calc(100% / n - k)` resolves its 100% against the track's CONTENT box,
    // so the measurement has to strip padding and border. `.gem-slider` has
    // neither today; doing it anyway keeps the identity true if it ever does.
    //
    // Two ways to measure it, and the difference is the whole bug:
    // `getBoundingClientRect()` reports pixels AFTER every ancestor transform,
    // while `clientWidth` reports the untransformed layout box -- which is what
    // the CSS `calc(100% / n)` this mirrors resolves against.
    //
    // The theme editor scales its preview to fit the pane. At scale(0.25) the
    // rect said 330px where the layout was 1318px, so the slides were pinned at
    // `min-width: 59.9px` -- a 60px column with the stars stacked and the copy
    // breaking one character per line. Worse, the value is baked inline: it
    // survived the transform being removed, so the section stayed collapsed.
    //
    // The rect is still preferred when the two AGREE, because it carries
    // sub-pixel precision that `clientWidth` rounds away, and the round trip is
    // gated at zero differing pixels against a render that has no transform.
    function contentWidth(el) {
      var cs = window.getComputedStyle(el);
      var padding = (parseFloat(cs.paddingLeft) || 0) +
                    (parseFloat(cs.paddingRight) || 0);
      var border = (parseFloat(cs.borderLeftWidth) || 0) +
                   (parseFloat(cs.borderRightWidth) || 0);
      var painted = el.getBoundingClientRect().width - padding - border;
      var laid = el.clientWidth - padding;   // clientWidth already excludes border
      if (laid > 0 && Math.abs(painted - laid) > 1) return laid;
      return painted;
    }

    function layout(animate, offset) {
      var bp = breakpoint();
      var width = contentWidth(track);
      if (!(width > 0)) return;
      var next = slideGeometry(slides[0], width, bp);
      if (!next) return;               // unknown shape: leave the DOM alone
      geom = next;
      for (var i = 0; i < slides.length; i++) {
        setStyle(slides[i], 'minWidth', px(geom.width));
        setStyle(slides[i], 'maxWidth', px(geom.width));
      }
      place(offset || 0, !!animate);
      paintDots(bp);
    }

    function goTo(index, animate) {
      if (!geom) { layout(false, 0); if (!geom) return; }
      var n = slides.length;
      current = loop ? ((index % n) + n) % n
                     : Math.max(0, Math.min(n - Math.ceil(geom.perView), index));
      layout(animate !== false, 0);
    }

    /* dots -- GemPages ships the container with `<!-- item render by js -->` */
    var dotsBox = root.querySelector('.gp-carousel-dot-container' +
                                     (uid ? '-' + uid : ''));
    var dots = [];

    function buildDots() {
      if (!dotsBox || dots.length) return;
      // GemPages ships the container holding only `<!-- item render by js -->`,
      // so there is normally nothing to clear. Clearing anyway makes a rebuild
      // idempotent no matter what markup the container comes back with -- the
      // alternative is a second strip of dots appended below the first.
      var stale = dotsBox.querySelectorAll('button.gp-carousel-dot');
      for (var s = 0; s < stale.length; s++) stale[s].remove();
      for (var i = 0; i < slides.length; i++) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'gp-carousel-dot';
        dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        dot.style.padding = '0';
        dot.style.border = '0';
        dot.style.borderRadius = '9999px';
        dot.style.cursor = 'pointer';
        ctx.listen(dot, 'click', (function (index) {
          return function () { goTo(index, true); };
        })(i));
        dotsBox.appendChild(dot);
        dots.push(dot);
      }
    }

    function paintDots(bp) {
      if (!dots.length) return;
      var size = px(parseFloat(pick(setting.dotSize, bp)) || 8);
      var idle = colour(pick(setting.dotColor, bp), '#E0E0E0');
      var live = colour(pick(setting.dotActiveColor, bp), '#242424');
      for (var i = 0; i < dots.length; i++) {
        setStyle(dots[i], 'width', size);
        setStyle(dots[i], 'height', size);
        setStyle(dots[i], 'flex', '0 0 auto');
        setStyle(dots[i], 'backgroundColor', i === current ? live : idle);
      }
    }

    // Every listener below goes through ctx.listen, including the ones on
    // nodes that would normally die with the section's own DOM. That is what
    // makes dispose() a complete undo of init(): a release() that turns out
    // NOT to have been a real DOM swap (the same nodes coming back) then
    // rebinds cleanly instead of leaving two closures fighting over `current`.

    /* arrows */
    function wireArrow(selector, delta) {
      var button = root.querySelector(selector);
      if (!button) return;
      ctx.listen(button, 'click', function (event) {
        event.preventDefault();
        goTo(current + delta, true);
      });
    }
    wireArrow('.gp-carousel-action-back', -1);
    wireArrow('.gp-carousel-action-next', 1);

    /* pointer drag / swipe */
    if (dragEnabled && window.PointerEvent) {
      var startX = 0;
      var dragging = false;
      track.style.touchAction = 'pan-y';
      ctx.listen(track, 'pointerdown', function (event) {
        if (event.button !== 0 || !geom) return;
        dragging = true;
        startX = event.clientX;
        try { track.setPointerCapture(event.pointerId); } catch (err) { /* ignore */ }
      });
      ctx.listen(track, 'pointermove', function (event) {
        if (!dragging) return;
        place(event.clientX - startX, false);
      });
      var endDrag = function (event) {
        if (!dragging) return;
        dragging = false;
        var dx = event.clientX - startX;
        var threshold = Math.min(geom.width * 0.2, 60);
        if (dx <= -threshold) goTo(current + 1, true);
        else if (dx >= threshold) goTo(current - 1, true);
        else layout(true, 0);
      };
      ctx.listen(track, 'pointerup', endDrag);
      ctx.listen(track, 'pointercancel', endDrag);
    }

    buildDots();
    layout(false, 0);

    // These two outlive the carousel's own DOM, so they are the pair that must
    // come off again when the editor re-renders the section.
    var pending = 0;
    ctx.listen(window, 'resize', function () {
      if (pending) return;
      pending = window.requestAnimationFrame(function () {
        pending = 0;
        layout(false, 0);
      });
    });
    ctx.listen(window, 'load', function () { layout(false, 0); });
    ctx.onCleanup(function () {
      if (pending) window.cancelAnimationFrame(pending);
    });
  }

  /* ------------------------------------------------------------- countdown */

  var CD_UNITS = [
    ['week', 'enableWeek', 604800000],
    ['day', 'enableDay', 86400000],
    ['hour', 'enableHour', 3600000],
    ['minute', 'enableMinute', 60000],
    ['second', 'enableSecond', 1000]
  ];

  function evergreenDuration(timer) {
    if (!timer) return 0;
    var num = function (v) { return parseFloat(v) || 0; };
    return ((num(timer.days) * 24 + num(timer.hours)) * 60 + num(timer.mins)) * 60000
           + num(timer.seconds) * 1000;
  }

  // Deadline policy (documented in TEMPLATIZE_NOTES.md): an evergreen timer is
  // per-visitor, so the first visit stamps `now + duration` into localStorage
  // under a key scoped to the element's GemPages uid, and every later visit
  // counts down to that same stamp. Private mode / file:// throw on storage,
  // in which case the deadline lives for the page view only.
  function deadlineFor(key, duration) {
    var now = Date.now();
    try {
      var stored = parseInt(window.localStorage.getItem(key), 10);
      if (stored && stored > 0 && stored - now <= duration) return stored;
      window.localStorage.setItem(key, String(now + duration));
    } catch (err) { /* storage unavailable -- session-only deadline */ }
    return now + duration;
  }

  function initCountdown(root, ctx) {
    var data = parseJSONAttr(root, 'gp-data') || {};
    var uid = data.uid || root.getAttribute('data-id') || 'countdown';
    // `behaviour` and `timerEverygreen` are GemPages' own spellings, verified
    // against capture/dom.html -- "Everygreen" is their typo, not ours.
    if (data.behaviour !== 'evergreen') return;   // only the captured mode
    var duration = evergreenDuration(data.timerEverygreen);
    if (duration <= 0) return;

    var units = CD_UNITS.filter(function (u) { return data[u[1]] !== false; });
    var fields = units.map(function (u) {
      var host = root.querySelector('#' + u[0] + ' .gp-countdown-number');
      return host ? { size: u[2], node: host } : null;
    }).filter(Boolean);
    if (!fields.length) return;

    var deadline = deadlineFor('lp-v2:countdown:' + uid, duration);

    function tick() {
      var left = Math.max(0, deadline - Date.now());
      for (var i = 0; i < fields.length; i++) {
        var field = fields[i];
        // Each field shows the remainder below the next ENABLED unit, so the
        // largest enabled one absorbs everything above it: with minutes and
        // seconds enabled (this page), 1h05m reads "65:00". A non-contiguous
        // enabled set (hours + seconds, no minutes) therefore lets the smaller
        // field run past 99 -- correct arithmetic, but the two-digit pad is
        // then cosmetic only. No captured page uses such a set.
        var value = Math.floor(left / field.size);
        if (i > 0) value = value % Math.floor(fields[i - 1].size / field.size);
        var text = value < 10 ? '0' + value : String(value);
        if (field.node.textContent !== text) field.node.textContent = text;
      }
      return left;
    }

    if (tick() > 0) {
      var handle = window.setInterval(function () {
        if (tick() <= 0) window.clearInterval(handle);
      }, 1000);
      // Without this, every settings edit in the editor would leave another
      // interval ticking against a detached node -- N edits, N timers.
      ctx.onCleanup(function () { window.clearInterval(handle); });
    }
  }

  /* ---------------------------------------------------------------- sticky */

  // Trigger semantics copied from the captured `<script id="custom-js-...">`:
  // first `.slide-sticky`, first `.heading-trigger`, reveal once the page has
  // scrolled past the trigger's document offset, hide again above it.
  // `sticky` is passed in by boot(); the trigger lives in a DIFFERENT section
  // from the bar, so it is still looked up across the whole document.
  function initSticky(sticky, ctx) {
    var trigger = document.querySelector('.heading-trigger');
    if (!trigger) return;

    var offset = 0;
    var measure = function () {
      // Re-queried every time rather than closed over. The trigger lives in a
      // DIFFERENT section, so the editor can replace it without this binding
      // being released -- and a detached node measures 0, which would drag the
      // reveal threshold down to wherever the page happened to be scrolled.
      var live = document.querySelector('.heading-trigger') || trigger;
      offset = live.getBoundingClientRect().top + window.scrollY;
    };
    var check = function () {
      if (window.scrollY > offset) sticky.classList.add('visible');
      else sticky.classList.remove('visible');
    };

    measure();
    ctx.listen(window, 'scroll', check, { passive: true });
    ctx.listen(window, 'resize', function () { measure(); check(); });
    ctx.listen(window, 'load', function () { measure(); check(); });

    // The trigger's offset moves whenever ANY section above it is re-rendered,
    // which is a reflow the bar's own section never hears about. The
    // section:load / section:unload handlers call this so it cannot go stale.
    stickyRefresh = function () { measure(); check(); };
    ctx.onCleanup(function () { stickyRefresh = null; });
  }

  /* ----------------------------------------------------------------- video */

  // `data-lp-autoplay` marks a motion/GIF-replacement clip that ships WITHOUT
  // the `autoplay` attribute (pipeline/verbatim/delivery.py `video_attributes`,
  // templatize `_video_slot_markup`, gifvideo `video_tag`) so a visitor never
  // downloads webm bytes for a clip that never scrolls into view. This is the
  // runtime half: play() once the element nears the viewport, pause() again
  // once it leaves. One observer is shared by every clip on the page rather
  // than one per element -- there is no per-element state IntersectionObserver
  // needs that a shared instance cannot carry via `entry.target`.
  //
  // `rootMargin: '200px 0px'` starts the clip decoding slightly before it is
  // actually visible, so the first visible frame is not a blank/poster frame
  // the visitor catches mid-buffer.
  var VIDEO_OBSERVER = null;

  function videoObserver() {
    if (VIDEO_OBSERVER) return VIDEO_OBSERVER;
    if (typeof IntersectionObserver === 'undefined') return null;
    VIDEO_OBSERVER = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        var entry = entries[i];
        var video = entry.target;
        if (entry.isIntersecting) {
          playSilently(video);
        } else {
          try { video.pause(); } catch (err) { /* ignore */ }
        }
      }
    }, { rootMargin: '200px 0px' });
    return VIDEO_OBSERVER;
  }

  // `play()` returns a promise that rejects when the browser's autoplay
  // policy refuses it or the clip has no playable source yet -- neither is
  // this file's problem to report, so both are swallowed the same way a bare
  // `autoplay` attribute's own (unobservable) failure always was.
  function playSilently(video) {
    try {
      var p = video.play();
      if (p && typeof p.catch === 'function') p.catch(function () {});
    } catch (err) { /* ignore -- same silent failure as a plain autoplay attribute */ }
  }

  function initVideoAutoplay(video, ctx) {
    var observer = videoObserver();
    if (observer) {
      observer.observe(video);
      ctx.onCleanup(function () { observer.unobserve(video); });
      return;
    }
    // No IntersectionObserver in this browser: reproduce the old `autoplay`
    // attribute's own behaviour exactly -- one play() call, fire and forget,
    // regardless of where the element sits on the page.
    playSilently(video);
  }

  /* ------------------------------------------------------------ theme editor */

  // Design mode only. `shopify:section:select` fires on the section wrapper
  // when the merchant clicks that section in the editor sidebar; it does not
  // exist on the storefront, and the guard below means this costs a live
  // visitor one `if` and nothing else.
  //
  // The scroll itself: Shopify's editor is documented to scroll the preview to
  // the selected section, but a theme can perform it too, and the two agree on
  // the target -- so doing it here makes the behaviour independent of which one
  // acts. What neither can do is scroll to an element with no layout box, which
  // is exactly what was wrong: GemPages' `.gps,.gpsi{display:contents}` reset
  // applied to the wrapper Shopify generates, so every section measured 0x0 at
  // (0,0) and every selection landed at the top of the page. The box is
  // restored in `layout/lp-v2-blank.liquid`; this handler is the other half,
  // and the `rect` check below keeps it honest if that rule is ever lost.
  function initEditor() {
    if (!(window.Shopify && window.Shopify.designMode)) return;

    document.addEventListener('shopify:section:select', function (event) {
      var el = event.target;
      if (!el || typeof el.getBoundingClientRect !== 'function') return;
      var rect = el.getBoundingClientRect();
      // A boxless wrapper would scroll to y=0 and *look* like the bug this
      // exists to fix, so leave the page where it is instead.
      if (!rect.height && !rect.width) return;
      if (typeof el.scrollIntoView !== 'function') return;
      // `behavior: 'instant'` explicitly: the captured reset puts
      // `scroll-behavior:smooth` on `.gps`, which the wrapper carries.
      el.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'instant' });
    });

    // A settings edit makes Shopify re-render that one section through the
    // Section Rendering API and swap its DOM in. The replacement carries the
    // captured markup but none of the listeners, so without this the carousel,
    // countdown and sticky bar go inert until the preview is reloaded by hand.
    //
    // Only `event.target` is rebound, never the whole page: the other sections'
    // DOM is untouched, and re-running boot() over them would be a no-op at
    // best (they are already flagged bound) and a double-bind at worst.
    document.addEventListener('shopify:section:load', function (event) {
      var section = event.target;
      if (!section || typeof section.querySelectorAll !== 'function') return;
      release(section);   // also collects the outgoing copy, now detached
      boot(section);
      // A reflow above the sticky bar's trigger changes its threshold even
      // when the bar's own section was not the one that reloaded.
      if (stickyRefresh) guard(stickyRefresh);
    });

    // Deleting or hiding a section: stop its interval and drop its window
    // listeners rather than leaving them bound to nodes nobody can see.
    document.addEventListener('shopify:section:unload', function (event) {
      release(event.target);
      if (stickyRefresh) guard(stickyRefresh);
    });
  }

  /* ------------------------------------------------------------------ boot */

  // Each component is isolated: a throw in one (a `gp-data` shape we have not
  // seen, a uid that is not a bare CSS identifier) must not take the other two
  // down with it -- bindOnce() carries that guard for the three components, and
  // this one covers the editor wiring. Failures are silent by design: the page
  // is a live landing page, not a console.
  function guard(fn, arg) {
    try { fn(arg); } catch (err) { /* one dead component, not three */ }
  }

  onReady(function () {
    boot(document);
    guard(initEditor);
  });
})();
